import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { workspaceApi } from '@/api/workspaceApi'
import { subscriptionApi } from '@/api/subscriptionApi'
import { invitationApi, type InvitationListParams } from '@/api/invitationApi'
import { memberApi, type MemberListParams } from '@/api/memberApi'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useAuthStore } from '@/stores/authStore'
import { usePlanDialogStore } from '@/stores/planDialogStore'
import { useCan } from '@/hooks/useCan'
import { useInfinitePaginatedList } from '@/hooks/useInfinitePaginatedList'
import { morphToast } from '@/components/ui/Toast'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { SiteShell } from '@/components/layout/SiteShell'
import { LeaveWorkspaceDialog } from '@/components/workspace/LeaveWorkspaceDialog'
import { DeleteWorkspaceDialog } from '@/components/workspace/DeleteWorkspaceDialog'
import { RemoveMemberDialog } from '@/components/workspace/RemoveMemberDialog'
import { InviteMemberDialog } from '@/components/workspace/InviteMemberDialog'
import {
  WorkspaceRoleValue,
  type ChargeHistoryItem,
  type WorkspaceInvitation,
  type WorkspaceRole,
  type WorkspaceMember,
} from '@/api/types'
import { BackIcon } from '@/components/ui/icons'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { formatDate } from '@/utils/date'
import { InfiniteScrollTable } from '@/components/workspace/InfiniteScrollTable'

type ManagerTab = 'info' | 'members' | 'charges'

export default function ManagerPage() {
  const { t } = useTranslation()
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') as ManagerTab | null) ?? 'info'

  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const subscription = useWorkspaceStore((s) => s.subscription)
  const myRole = useWorkspaceStore((s) => s.myRole)
  const openPlanDialog = usePlanDialogStore((s) => s.openDialog)
  const loadWorkspaceBySlug = useWorkspaceStore((s) => s.loadWorkspaceBySlug)
  const loadSubscription = useWorkspaceStore((s) => s.loadSubscription)
  const inviteMember = useWorkspaceStore((s) => s.inviteMember)
  const updateMember = useWorkspaceStore((s) => s.updateMember)

  const removeMemberAction = useWorkspaceStore((s) => s.removeMember)
  const leaveCurrentWorkspace = useWorkspaceStore((s) => s.leaveCurrentWorkspace)
  const deleteCurrentWorkspace = useWorkspaceStore((s) => s.deleteCurrentWorkspace)

  const currentUserId = useAuthStore((s) => s.user?.id ?? null)

  const canManageMembers = useCan('manage_members')
  const canManageWorkspace = useCan('manage_workspace')
  const workspaceId = currentWorkspace?.id
  const isOnMembersTab = activeTab === 'members'
  const isOwner = myRole === WorkspaceRoleValue.OWNER
  const isOnChargesTab = activeTab === 'charges' && isOwner

  const {
    items: pagedMembers,
    total: membersTotal,
    loading: loadingPagedMembers,
    loadingMore: loadingMoreMembers,
    hasMore: hasMoreMembers,
    filters: memberFilters,
    setFilter: setMemberFilter,
    loadMore: loadMoreMembers,
    reload: reloadMembers,
    patchItems: patchMemberItems,
  } = useInfinitePaginatedList<WorkspaceMember, MemberListParams>(
    (params) => memberApi.list(workspaceId!, params),
    {
      initialLimit: 10,
      initialFilters: {},
      enabled: isOnMembersTab && !!workspaceId,
    },
  )

  const {
    items: pendingInvitations,
    total: invitationsTotal,
    loading: loadingInvitations,
    loadingMore: loadingMoreInvitations,
    hasMore: hasMoreInvitations,
    loadMore: loadMoreInvitations,
    reload: reloadInvitations,
    patchItems: patchInvitationItems,
    removeItems: removeInvitationItems,
  } = useInfinitePaginatedList<WorkspaceInvitation, InvitationListParams>(
    (params) => invitationApi.listForWorkspace(workspaceId!, params, { silent: true }),
    {
      initialLimit: 10,
      initialFilters: {},
      enabled: isOnMembersTab && !!workspaceId && canManageMembers,
    },
  )

  const {
    items: charges,
    total: chargesTotal,
    loading: loadingCharges,
    loadingMore: loadingMoreCharges,
    hasMore: hasMoreCharges,
    loadMore: loadMoreCharges,
  } = useInfinitePaginatedList<ChargeHistoryItem, Record<string, never>>(
    (params) => subscriptionApi.charges(workspaceId!, params),
    {
      initialLimit: 20,
      initialFilters: {},
      enabled: isOnChargesTab && !!workspaceId,
    },
  )

  const [workspaceName, setWorkspaceName] = useState('')
  const [workspaceSlugValue, setWorkspaceSlugValue] = useState('')
  const [slugEditEnabled, setSlugEditEnabled] = useState(false)
  const [slugConfirmOpen, setSlugConfirmOpen] = useState(false)
  const slugInputRef = useRef<HTMLInputElement>(null)
  const pendingWorkspaceSlugRef = useRef<string | null>(null)
  const [savingInfo, setSavingInfo] = useState(false)
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [removingMember, setRemovingMember] = useState<WorkspaceMember | null>(null)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [invitationActionId, setInvitationActionId] = useState<number | null>(null)
  const [activeOwnerCount, setActiveOwnerCount] = useState(0)

  useEffect(() => {
    if (!workspaceSlug) return
    if (currentWorkspace?.slug === workspaceSlug) {
      pendingWorkspaceSlugRef.current = null
      return
    }
    if (pendingWorkspaceSlugRef.current === currentWorkspace?.slug) return
    void loadWorkspaceBySlug(workspaceSlug).catch(() => navigate('/workspaces', { replace: true }))
  }, [currentWorkspace?.slug, loadWorkspaceBySlug, navigate, workspaceSlug])

  useEffect(() => {
    if (!currentWorkspace?.id) return
    setWorkspaceName(currentWorkspace.name)
    setWorkspaceSlugValue(currentWorkspace.slug)
    void loadSubscription(currentWorkspace.id)
  }, [currentWorkspace?.id, currentWorkspace?.name, currentWorkspace?.slug, loadSubscription])

  const refreshActiveOwnerCount = useCallback(async () => {
    if (!workspaceId) return
    try {
      const page = await memberApi.list(
        workspaceId,
        { limit: 10, role: WorkspaceRoleValue.OWNER },
        { silent: true },
      )
      setActiveOwnerCount(page.count)
    } catch {
      /* The backend still enforces the last-owner invariant. */
    }
  }, [workspaceId])

  useEffect(() => {
    void refreshActiveOwnerCount()
  }, [refreshActiveOwnerCount])
  const teamAllowedByPlan =
    subscription?.plan.allow_team_members ?? subscription?.plan.allows_team
  const teamDisabledByPlan = teamAllowedByPlan === false
  const inviteBlocked = teamDisabledByPlan
  const inviteBlockMessage = teamDisabledByPlan
    ? t('members.invite.planLimit')
    : null
  const roleOptions = useMemo(
    () => [
      { value: WorkspaceRoleValue.OWNER, label: t('members.role.owner'), description: 'Proprietário do workspace. Acesso total.' },
      { value: WorkspaceRoleValue.ADMIN, label: t('members.role.admin'), description: 'Gerencia membros e configurações do workspace.' },
      { value: WorkspaceRoleValue.EMPLOYEE, label: t('members.role.employee'), description: 'Cria projetos e colabora nos que é membro.' },
    ],
    [t],
  )

  async function handleSaveInfo() {
    if (!currentWorkspace) return
    setSavingInfo(true)
    try {
      const previousSlug = currentWorkspace.slug
      const newSlug = workspaceSlugValue.trim() || currentWorkspace.slug
      const body: { name?: string; slug?: string } = {
        name: workspaceName.trim() || currentWorkspace.name,
      }
      if (newSlug !== currentWorkspace.slug) body.slug = newSlug
      const updated = await workspaceApi.patch(currentWorkspace.id, body)
      if (updated.slug !== previousSlug) pendingWorkspaceSlugRef.current = updated.slug
      useWorkspaceStore.setState((state) => ({
        currentWorkspace: updated,
        workspaces: state.workspaces.map((item) => (item.id === updated.id ? updated : item)),
      }))
      useAuthStore.setState((state) => ({
        memberships: state.memberships.map((membership) =>
          membership.workspace_id === updated.id
            ? {
                ...membership,
                workspace_name: updated.name,
                workspace_slug: updated.slug,
              }
            : membership,
        ),
      }))
      if (updated.slug !== previousSlug) {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('morpherd_last_workspace_slug', updated.slug)
        }
      }
      setSlugEditEnabled(false)
      morphToast.success(t('workspace.updated'))
      if (body.slug) {
        navigate(`/workspace/${updated.slug}/manager`, { replace: true })
      }
    } catch (err) {
      morphToast.fromError(err)
    } finally {
      setSavingInfo(false)
    }
  }

  async function handleInvite(email: string, role: WorkspaceRole) {
    if (inviteBlocked) {
      morphToast.warning(
        inviteBlockMessage ??
          t('members.invite.notAllowedByPlan', 'Convites indisponíveis neste plano.'),
      )
      throw new Error('plan_blocked')
    }
    const invitation = await inviteMember({ email, role })
    reloadInvitations()
    return invitation
  }

  async function handleResendInvitation(invitationId: number) {
    if (!workspaceId) return
    setInvitationActionId(invitationId)
    try {
      const updated = await invitationApi.resend(workspaceId, invitationId, { silent: true })
      patchInvitationItems((current) =>
        current.map((item) => (item.id === invitationId ? updated : item)),
      )
      morphToast.success(t('members.pending.resent', 'Convite reenviado.'))
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setInvitationActionId(null)
    }
  }

  async function handleCancelInvitation(invitationId: number) {
    if (!workspaceId) return
    setInvitationActionId(invitationId)
    try {
      await invitationApi.cancel(workspaceId, invitationId, { silent: true })
      removeInvitationItems((item) => item.id === invitationId)
      morphToast.success(t('members.pending.cancelled', 'Convite cancelado.'))
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setInvitationActionId(null)
    }
  }

  async function handleUpdateMember(memberId: number, body: { role?: WorkspaceRole; is_active?: boolean }) {
    try {
      const updated = await updateMember(memberId, body)
      patchMemberItems((items) => items.map((m) => (m.id === memberId ? updated : m)))
      void refreshActiveOwnerCount()
      morphToast.success(t('members.updated', 'Membro atualizado.'))
    } catch (err) {
      morphToast.fromError(err)
    }
  }

  async function handleRemoveMember(memberId: number) {
    try {
      await removeMemberAction(memberId)
      morphToast.success(t('members.removed'))
      reloadMembers()
      void refreshActiveOwnerCount()
    } catch (err) {
      morphToast.fromError(err)
      throw err
    }
  }

  async function handleLeave() {
    try {
      await leaveCurrentWorkspace()
      navigate('/workspaces', { replace: true })
    } catch (err) {
      morphToast.fromError(err)
    }
  }

  async function handleDelete() {
    try {
      await deleteCurrentWorkspace()
      navigate('/workspaces', { replace: true })
    } catch (err) {
      morphToast.fromError(err)
    }
  }

  const containerStyle: React.CSSProperties = {
    flex: 1,
    background: 'var(--bg-app)',
    color: 'var(--text)',
    fontFamily: 'system-ui, sans-serif',
  }

  return (
    <SiteShell>
      <div style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 32px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={() => navigate(`/workspace/${workspaceSlug}/projects`)}
              aria-label={t('common.back', 'Voltar')}
              title={t('common.back', 'Voltar')}
              style={{
                ...ghostBtn,
                width: 32,
                padding: 0,
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BackIcon />
            </button>
            <div>
              <h1 style={{ margin: 0, fontSize: 22 }}>{t('workspace.manage')}</h1>
              <p style={{ margin: '4px 0 0', opacity: 0.55 }}>{currentWorkspace?.name}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setLeaveDialogOpen(true)}
              disabled={activeOwnerCount <= 1 && myRole === WorkspaceRoleValue.OWNER}
              title={activeOwnerCount <= 1 && myRole === WorkspaceRoleValue.OWNER ? t('members.lastOwnerLeaveHint') : undefined}
              style={{
                ...ghostBtn,
                border: '1px solid var(--color-danger-border)',
                color: 'var(--color-danger)',
                opacity: activeOwnerCount <= 1 && myRole === WorkspaceRoleValue.OWNER ? 0.45 : 1,
                cursor: activeOwnerCount <= 1 && myRole === WorkspaceRoleValue.OWNER ? 'not-allowed' : 'pointer',
              }}
            >
              {t('workspace.leave')}
            </button>
            {myRole === WorkspaceRoleValue.OWNER && (
              <button
                onClick={() => openPlanDialog()}
                style={{
                  ...ghostBtn,
                  border: '1px solid var(--accent)',
                  background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
                  color: 'var(--accent)',
                }}
              >
                {t('billing.changePlan')}
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '16px 32px 0',
          }}
        >
          {(['info', 'members', ...(isOwner ? ['charges'] : [])] as ManagerTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setSearchParams({ tab })}
              style={tab === activeTab ? activeTabBtn : ghostBtn}
            >
              {tab === 'info'
                ? t('workspace.info')
                : tab === 'members'
                  ? t('members.title')
                  : t('billing.charges.title', 'Cobranças')}
            </button>
          ))}
          {isOnMembersTab && canManageMembers ? (
            <button
              onClick={() => setInviteDialogOpen(true)}
              style={{ ...primaryBtn, marginLeft: 'auto', opacity: inviteBlocked ? 0.5 : 1 }}
              disabled={inviteBlocked}
            >
              {t('members.invite.cta', '+ Convidar membro')}
            </button>
          ) : null}
        </div>

        <div style={{ padding: '24px 32px' }}>
          {activeTab === 'info' ? (
            <>
              <section style={panelStyle}>
                <label style={labelStyle} htmlFor="workspace-name-input">{t('common.name')}</label>
                <input id="workspace-name-input" value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} className="morph-input" disabled={!canManageWorkspace || savingInfo} />
                <label style={{ ...labelStyle, marginTop: 16 }} htmlFor="workspace-slug-input">{t('workspace.slug')}</label>
                <input
                  ref={slugInputRef}
                  id="workspace-slug-input"
                  value={workspaceSlugValue}
                  onChange={(e) => setWorkspaceSlugValue(e.target.value)}
                  className="morph-input"
                  disabled={!canManageWorkspace || savingInfo}
                  readOnly={!slugEditEnabled}
                  onClick={() => {
                    if (canManageWorkspace && !slugEditEnabled && !savingInfo) setSlugConfirmOpen(true)
                  }}
                  style={{ cursor: canManageWorkspace && !slugEditEnabled ? 'pointer' : undefined }}
                />
                {slugConfirmOpen && (
                  <div style={{
                    marginTop: 10,
                    border: '1px solid var(--color-warning-border)',
                    background: 'var(--color-warning-bg)',
                    borderRadius: 8,
                    padding: '12px 14px',
                    fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-warning)', marginBottom: 6 }}>
                      {t('workspace.slugChangeWarningTitle', 'Atenção: o slug será alterado')}
                    </div>
                    <p style={{ margin: '0 0 10px', opacity: 0.8, lineHeight: 1.5 }}>
                      {t('workspace.slugChangeWarning', 'Ao trocar o slug, todas as URLs de acesso aos projetos deste workspace serão alteradas. Links existentes vão parar de funcionar e quem estiver usando será afetado.')}
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => {
                          setSlugConfirmOpen(false)
                          setSlugEditEnabled(true)
                          setTimeout(() => slugInputRef.current?.focus(), 0)
                        }}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--color-warning-border)',
                          color: 'var(--color-warning)',
                          borderRadius: 6,
                          padding: '5px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        {t('workspace.slugChangeConfirm', 'Entendi, quero editar')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSlugConfirmOpen(false)}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--border)',
                          color: 'var(--text)',
                          borderRadius: 6,
                          padding: '5px 12px',
                          fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
                {canManageWorkspace ? (
                  <div style={{ marginTop: 16 }}>
                    <button onClick={() => void handleSaveInfo()} style={primaryBtn} disabled={savingInfo}>{t('common.save')}</button>
                  </div>
                ) : null}
              </section>

              {myRole === WorkspaceRoleValue.OWNER ? (
                <section style={dangerPanelStyle}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-danger)', marginBottom: 4 }}>
                      {t('common.dangerZone')}
                    </div>
                    <p style={{ margin: 0, fontSize: 12, opacity: 0.65 }}>
                      {t('workspace.deleteDescription', 'Ações irreversíveis que afetam permanentemente este workspace.')}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid var(--color-danger-border)' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{t('workspace.delete')}</div>
                      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>
                        {t('workspace.deleteHint', 'Remove permanentemente o workspace e todos os seus dados.')}
                      </div>
                    </div>
                    <button
                      onClick={() => setDeleteDialogOpen(true)}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--color-danger-border)',
                        color: 'var(--color-danger)',
                        borderRadius: 8,
                        padding: '7px 14px',
                        cursor: 'pointer',
                        fontSize: 13,
                        flexShrink: 0,
                        marginLeft: 16,
                      }}
                    >
                      {t('workspace.delete')}
                    </button>
                  </div>
                </section>
              ) : null}
            </>
          ) : null}

          {activeTab === 'members' ? (
            <>
            <section style={panelStyle}>
              {canManageMembers && (
                <>
                  {inviteBlockMessage && (
                    <div
                      style={{
                        marginBottom: 16,
                        border: '1px solid var(--color-warning-border)',
                        background: 'var(--color-warning-bg)',
                        color: 'var(--color-warning)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        fontSize: 13,
                      }}
                    >
                      {inviteBlockMessage}
                    </div>
                  )}
                </>
              )}

              {/* Busca server-side */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <input
                  type="text"
                  value={memberFilters.q ?? ''}
                  onChange={(e) => setMemberFilter('q', e.target.value)}
                  placeholder={t('members.search')}
                  className="morph-input"
                  style={{ maxWidth: 520 }}
                />
                <div style={{ width: 190 }}>
                  <MorphSelect<WorkspaceRole>
                    value={memberFilters.role ?? null}
                    onChange={(v) =>
                      setMemberFilter('role', v !== null ? v : undefined as unknown as WorkspaceRole)
                    }
                    options={roleOptions}
                    clearable
                    placeholder={t('members.allRoles')}
                    size="md"
                    ariaLabel={t('members.allRoles')}
                  />
                </div>
              </div>

              <InfiniteScrollTable
                loadedCount={pagedMembers.length}
                totalCount={membersTotal}
                loading={loadingPagedMembers}
                loadingMore={loadingMoreMembers}
                hasMore={hasMoreMembers}
                onLoadMore={loadMoreMembers}
                ariaLabel={t('members.pagination.membersAria')}
                testId="members-list"
                empty={!loadingPagedMembers && pagedMembers.length === 0}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead style={stickyHeaderStyle}>
                    <tr>
                      <th style={{ ...scrollThStyle, width: '38%' }}>{t('members.table.user', 'Usuário')}</th>
                      <th style={{ ...scrollThStyle, width: '25%' }}>{t('members.table.role', 'Função')}</th>
                      <th style={{ ...scrollThStyle, width: '19%' }}>{t('members.table.joinedAt', 'Entrou em')}</th>
                      <th style={{ ...scrollThStyle, width: '18%' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {loadingPagedMembers && pagedMembers.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={emptyCellStyle}>{t('common.loading', 'Carregando...')}</td>
                      </tr>
                    ) : pagedMembers.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={emptyCellStyle}>{t('members.pagination.emptyMembers')}</td>
                      </tr>
                    ) : pagedMembers.map((member) => {
                        const isLastOwner =
                          member.role === WorkspaceRoleValue.OWNER && activeOwnerCount <= 1
                        const isSelf = member.user.id === currentUserId
                        const canEdit =
                          canManageMembers && myRole !== null && myRole <= member.role
                        const canRemove = canEdit && !isLastOwner && !isSelf
                        return (
                          <tr key={member.id} style={{ borderTop: '1px solid var(--border)', height: 58 }}>
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <UserAvatar
                                  userId={member.user.id}
                                  name={member.user.name}
                                  email={member.user.email}
                                  size={30}
                                />
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {member.user.name || member.user.email}
                                    {isSelf && (
                                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid var(--accent-border)', whiteSpace: 'nowrap' }}>
                                        Você
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ opacity: 0.55, fontSize: 12 }}>{member.user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td style={tdStyle}>
                              {canEdit ? (
                                <div style={{ width: 190 }}>
                                  <MorphSelect<WorkspaceRole>
                                    value={member.role}
                                    onChange={(v) => {
                                      if (v !== null) void handleUpdateMember(member.id, { role: v })
                                    }}
                                    options={roleOptions}
                                    disabled={isLastOwner}
                                    size="sm"
                                    ariaLabel={t('members.table.role', 'Função')}
                                  />
                                </div>
                              ) : (
                                roleOptions.find((option) => option.value === member.role)?.label
                              )}
                            </td>
                            <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                              {formatDate(member.created_at)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                                <button
                                  onClick={() => setRemovingMember(member)}
                                  disabled={!canRemove}
                                  title={
                                    isLastOwner
                                      ? t('members.lastOwnerHint')
                                      : isSelf
                                        ? t('members.selfRemoveHint')
                                        : undefined
                                  }
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid var(--color-danger-border)',
                                    color: 'var(--color-danger)',
                                    borderRadius: 6,
                                    padding: '3px 10px',
                                    fontSize: 11,
                                    cursor: canRemove ? 'pointer' : 'not-allowed',
                                    opacity: canRemove ? 1 : 0.4,
                                  }}
                                >
                                  {t('members.remove')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </InfiniteScrollTable>
            </section>
            {canManageMembers ? (
              <section style={{ ...panelStyle, marginTop: 16 }}>
                <div style={{ marginBottom: 14 }}>
                  <h2 style={{ margin: 0, fontSize: 15 }}>
                    {t('members.pending.title', 'Convites pendentes')}
                  </h2>
                  <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.6 }}>
                    {t(
                      'members.pending.description',
                      'Acompanhe, reenvie ou cancele convites que ainda não foram respondidos.',
                    )}
                  </p>
                </div>
                <InfiniteScrollTable
                  loadedCount={pendingInvitations.length}
                  totalCount={invitationsTotal}
                  loading={loadingInvitations}
                  loadingMore={loadingMoreInvitations}
                  hasMore={hasMoreInvitations}
                  onLoadMore={loadMoreInvitations}
                  ariaLabel={t('members.pagination.invitationsAria')}
                  testId="invitations-list"
                  empty={!loadingInvitations && pendingInvitations.length === 0}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead style={stickyHeaderStyle}>
                      <tr>
                        <th style={{ ...scrollThStyle, width: '34%' }}>{t('members.pending.email', 'E-mail')}</th>
                        <th style={{ ...scrollThStyle, width: '22%' }}>{t('members.table.role', 'Função')}</th>
                        <th style={{ ...scrollThStyle, width: '20%' }}>{t('members.pending.sentAt', 'Último envio')}</th>
                        <th style={{ ...scrollThStyle, width: '24%' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {loadingInvitations && pendingInvitations.length === 0 ? (
                        <tr>
                          <td colSpan={4} style={emptyCellStyle}>{t('common.loading', 'Carregando...')}</td>
                        </tr>
                      ) : pendingInvitations.length === 0 ? (
                        <tr>
                          <td colSpan={4} style={emptyCellStyle}>{t('members.pending.empty', 'Nenhum convite pendente.')}</td>
                        </tr>
                      ) : pendingInvitations.map((invitation) => {
                        const busy = invitationActionId === invitation.id
                        return (
                          <tr key={invitation.id} style={{ borderTop: '1px solid var(--border)', height: 58 }}>
                            <td style={tdStyle}>{invitation.email}</td>
                            <td style={tdStyle}>
                              {roleOptions.find((option) => option.value === invitation.role)?.label}
                            </td>
                            <td style={{ ...tdStyle, opacity: 0.7 }}>
                              {formatDate(invitation.modified_at)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <button
                                  type="button"
                                  onClick={() => void handleResendInvitation(invitation.id)}
                                  disabled={busy}
                                  style={smallActionBtn}
                                >
                                  {t('members.pending.resend', 'Reenviar')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleCancelInvitation(invitation.id)}
                                  disabled={busy}
                                  style={{
                                    ...smallActionBtn,
                                    borderColor: 'var(--color-danger-border)',
                                    color: 'var(--color-danger)',
                                  }}
                                >
                                  {t('members.pending.cancel', 'Cancelar')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </InfiniteScrollTable>
              </section>
            ) : null}
            </>
          ) : null}

          {isOnChargesTab ? (
            <section style={panelStyle} data-testid="charges-panel">
              <div style={{ marginBottom: 14 }}>
                <h2 style={{ margin: 0, fontSize: 15 }}>
                  {t('billing.charges.title', 'Cobranças')}
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.6 }}>
                  {t(
                    'billing.charges.description',
                    'Histórico de pagamentos deste workspace.',
                  )}
                </p>
              </div>
              <InfiniteScrollTable
                loadedCount={charges.length}
                totalCount={chargesTotal}
                loading={loadingCharges}
                loadingMore={loadingMoreCharges}
                hasMore={hasMoreCharges}
                onLoadMore={loadMoreCharges}
                ariaLabel={t('billing.charges.title', 'Cobranças')}
                testId="charges-list"
                empty={!loadingCharges && charges.length === 0}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead style={stickyHeaderStyle}>
                    <tr>
                      <th style={{ ...scrollThStyle, width: '22%' }}>{t('billing.charges.date', 'Data')}</th>
                      <th style={{ ...scrollThStyle, width: '20%' }}>{t('billing.charges.plan', 'Plano')}</th>
                      <th style={{ ...scrollThStyle, width: '28%' }}>{t('billing.charges.period', 'Período')}</th>
                      <th style={{ ...scrollThStyle, width: '15%' }}>{t('billing.charges.status', 'Status')}</th>
                      <th style={{ ...scrollThStyle, width: '15%', textAlign: 'right' }}>{t('billing.charges.amount', 'Valor')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingCharges && charges.length === 0 ? (
                      <tr><td colSpan={5} style={emptyCellStyle}>{t('common.loading', 'Carregando...')}</td></tr>
                    ) : charges.length === 0 ? (
                      <tr><td colSpan={5} style={emptyCellStyle}>{t('billing.charges.empty', 'Nenhuma cobrança encontrada.')}</td></tr>
                    ) : charges.map((charge) => (
                      <tr key={charge.id} style={{ borderTop: '1px solid var(--border)', height: 58 }}>
                        <td style={tdStyle}>{formatDate(charge.paid_at ?? charge.created_at)}</td>
                        <td style={tdStyle}>{charge.plan_name || charge.plan_code || '—'}</td>
                        <td style={tdStyle}>
                          {charge.billing_period_start && charge.billing_period_end
                            ? `${formatDate(charge.billing_period_start)} – ${formatDate(charge.billing_period_end)}`
                            : '—'}
                        </td>
                        <td style={tdStyle}>{t(`billing.charges.statuses.${charge.status}`, charge.status)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                          {new Intl.NumberFormat(undefined, {
                            style: 'currency',
                            currency: charge.currency || 'BRL',
                          }).format(charge.amount_cents / 100)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </InfiniteScrollTable>
            </section>
          ) : null}
        </div>
      </div>

      <InviteMemberDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onInvite={handleInvite}
        excludeUserIds={pagedMembers.map((m) => m.user.id)}
        canInviteOwner={myRole === WorkspaceRoleValue.OWNER}
      />

      <RemoveMemberDialog
        open={removingMember !== null}
        onClose={() => setRemovingMember(null)}
        member={removingMember}
        onConfirm={handleRemoveMember}
      />

      <LeaveWorkspaceDialog
        open={leaveDialogOpen}
        onClose={() => setLeaveDialogOpen(false)}
        workspaceName={currentWorkspace?.name ?? ''}
        onConfirm={handleLeave}
      />

      <DeleteWorkspaceDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        workspaceName={currentWorkspace?.name ?? ''}
        onConfirm={handleDelete}
      />
    </SiteShell>
  )
}

const panelStyle: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 20,
}

const dangerPanelStyle: React.CSSProperties = {
  marginTop: 16,
  background: 'color-mix(in srgb, var(--color-danger) 5%, var(--bg-card))',
  border: '1px solid var(--color-danger-border)',
  borderRadius: 10,
  padding: 20,
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontSize: 12,
  opacity: 0.7,
}

const primaryBtn: React.CSSProperties = {
  background: '#3b82f6',
  border: '1px solid #3b82f6',
  color: '#fff',
  borderRadius: 8,
  height: 30,
  padding: '0 14px',
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
}

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  borderRadius: 8,
  height: 30,
  padding: '0 14px',
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
}

const smallActionBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 11,
  cursor: 'pointer',
}

const activeTabBtn: React.CSSProperties = {
  ...ghostBtn,
  background: 'color-mix(in srgb, var(--accent) 10%, var(--bg-card))',
  border: '1px solid var(--accent)',
  color: 'var(--accent)',
}

const stickyHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  background: 'var(--bg-card)',
  boxShadow: '0 1px 0 var(--border)',
}

const scrollThStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 8px',
  opacity: 0.7,
  fontSize: 12,
  fontWeight: 500,
}

const tdStyle: React.CSSProperties = {
  padding: '8px',
  verticalAlign: 'middle',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const emptyCellStyle: React.CSSProperties = {
  height: 112,
  padding: 24,
  textAlign: 'center',
  color: 'var(--text-muted)',
  fontSize: 13,
}
